import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import {
  ResourceIndexView,
  ResourceType,
  SortDirection,
  useResourcesIndexViewQuery,
} from '@app/types/blackroom-api';
import { ResourceFoldersIndexViewProps } from './types';
import { useMemo } from 'react';
import { transformToNestedData } from '../ResourceList/utils/transformToNestedData';
import CollapsableResourceFolder from './CollapsableResourceFolder';
import { vars } from '@app/shared/themes/vars';
import { Box, Skeleton, Text } from '@mantine/core';
import { palette, surfaces } from '@app/shared/ui-kit/colors';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';

function ResourceFoldersIndexView(props: ResourceFoldersIndexViewProps) {
  const workspace = useWorkspace();

  const { data, loading: isFetching } = useResourcesIndexViewQuery({
    variables: {
      ...{
        filter: {
          workspaceId: workspace.id,
          type: ResourceType.Folder,
        },
        sorter: { index: SortDirection.Asc },
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // Transform the data once when it changes
  const nestedData = useMemo(() => {
    const transformedData = transformToNestedData(
      (data?.resourcesIndexView ?? []) as ResourceIndexView[],
    );
    return transformedData;
  }, [data]);

  return (
    <Box
      style={{
        scrollbarColor: `${vars.colors.blackroomBlack} ${vars.colors.blackroomWhite}`,
        scrollbarWidth: 'thin',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <Box
        px="1.65rem"
        py="0.75rem"
        style={{
          backgroundColor: surfaces.sunken,
          borderTopLeftRadius: vars.radius.sm,
          borderTopRightRadius: vars.radius.sm,
        }}
      >
        <Text
          style={{ fontSize: '0.78rem', color: palette.blackroomLightGray2 }}
          fw={FONT_WEIGHT.lighter}
        >
          Index
        </Text>
      </Box>
      {isFetching
        ? Array.from({ length: 2 }).map((_, index) => (
            <Box key={index} p="md">
              <Skeleton height={24} mb="xs" radius="xs" />
              <Box pl="md">
                <Skeleton height={20} mb="xs" radius="xs" width="80%" />
                <Skeleton height={20} mb="xs" radius="xs" width="60%" />
              </Box>
            </Box>
          ))
        : nestedData.map((r) => (
            <CollapsableResourceFolder
              key={r.id}
              resource={r}
              level={0}
              onFolderClick={props.onFolderClick}
            />
          ))}
    </Box>
  );
}

export default ResourceFoldersIndexView;
